import {
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { locationData } from "../aircraft/state";
import {
  errorHasMessage,
  Locations,
  Region,
  SnackData,
} from "../aircraft/types";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  generateMissionThunk,
  getRoutesThunk,
} from "../mission/missionSlice";
import {
  bindThunk,
  issueCommandThunk,
  pingThunk,
} from "../aircraft/aircraftSlice";
import {
  getGeofenceListThunk,
  listLandingPadsThunk,
} from "../airspace/airspaceSlice";
import { getCells, getRegions } from "./sessionAPI";
import { Cell } from "@m4a3/api-client/dist/models/cell.interface";

export interface SessionState {
  location: Locations;
  notification: SnackData | null;
  requests: {
    requestId: string;
    requestStatus: "pending" | "fulfilled" | "rejected";
    request: string;
  }[];
  username?: string;
  roles: string[];
  cells: Cell[];
  cell?: Cell;
  regions: Region[];
  region?: Region;
}

const initialState: SessionState = {
  location: "moorock",
  notification: null,
  requests: [],
  roles: [],
  cells: [],
  regions: [],
};

export const getCellsThunk = createAsyncThunk<
  Cell[],
  string,
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("session/getCells", async (payload, thunkApi) => {
  try {
    const response = await getCells(payload);
    return thunkApi.fulfillWithValue(response, {});
  } catch (error) {
    console.error(error);

    if (errorHasMessage(error)) {
      return thunkApi.rejectWithValue(error, {
        message: "Get cells failed: " + error?.message,
      });
    }
    return thunkApi.rejectWithValue(error, {
      message: "Get cells failed",
    });
  }
});
export const getRegionsThunk = createAsyncThunk<
  Region[],
  undefined,
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("session/getRegions", async (payload, thunkApi) => {
  try {
    const response = await getRegions();

    return thunkApi.fulfillWithValue(response, {});
  } catch (error) {
    console.error(error);

    if (errorHasMessage(error)) {
      return thunkApi.rejectWithValue(error, {
        message: "Get regions failed: " + error?.message,
      });
    }
    return thunkApi.rejectWithValue(error, {
      message: "Get regions failed",
    });
  }
});

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setLocation: (state, action: PayloadAction<Locations>) => {
      state.location = action.payload;
    },
    setCell: (state, action: PayloadAction<Cell>) => {
      state.cell = action.payload;
    },
    setRegion: (state, action: PayloadAction<Region>) => {
      state.region = action.payload;
      state.cells = [];
    },
    showSnackbar: (state, action: PayloadAction<SnackData>) => {
      state.notification = action.payload;
    },
    dismissSnackbar: (state) => {
      state.notification = null;
    },
    setUsername: (
      state,
      action: PayloadAction<{ username: string | undefined; roles: string[] }>
    ) => {
      state.username = action.payload.username;
      state.roles = action.payload.roles;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCellsThunk.fulfilled, (state, action) => {
      state.cells = action.payload;
      const cell = action.payload.find(
        (cell) => cell.region === state.region?.regionName
      );
      if (!state.cell && cell) state.cell = cell;
    });
    builder.addCase(getRegionsThunk.fulfilled, (state, action) => {
      state.regions = action.payload;

      if (!state.region && action.payload[1]) state.region = action.payload[1];
    });
    builder.addMatcher(
      isAnyOf(
        bindThunk.pending,
        pingThunk.pending,
        getRoutesThunk.pending,
        issueCommandThunk.pending,
        generateMissionThunk.pending,
        listLandingPadsThunk.pending,
        getGeofenceListThunk.pending
      ),
      (state, action) => {
        state.requests.push({
          requestId: action.meta.requestId,
          requestStatus: action.meta.requestStatus,
          request: action.type,
        });
        state.requests.slice(-10);
      }
    );
    builder.addMatcher(
      isAnyOf(
        bindThunk.fulfilled,
        pingThunk.fulfilled,
        getRoutesThunk.fulfilled,
        issueCommandThunk.fulfilled,
        generateMissionThunk.fulfilled,
        listLandingPadsThunk.fulfilled,
        getGeofenceListThunk.fulfilled,
        bindThunk.rejected,
        pingThunk.rejected,
        getRoutesThunk.rejected,
        issueCommandThunk.rejected,
        generateMissionThunk.rejected,
        listLandingPadsThunk.rejected,
        getGeofenceListThunk.rejected
      ),
      (state, action) => {
        const i = state.requests.findIndex(
          (request) => request.requestId === action.meta.requestId
        );
        state.requests.splice(i);
      }
    );
  },
});

export const {
  setLocation,
  showSnackbar,
  dismissSnackbar,
  setUsername,
  setCell,
  setRegion,
} = sessionSlice.actions;

export const selectLocation = (state: RootState) => state.session.location;
export const selectUsername = (state: RootState) => state.session.username;
export const selectRequests = (state: RootState) => state.session.requests;
export const selectCells = (state: RootState) => state.session.cells;
export const selectCell = (state: RootState) => state.session.cell;
export const selectRegions = (state: RootState) => state.session.regions;
export const selectRegion = (state: RootState) => state.session.region;
const selectRequestId = (state: RootState, requestId: string) => requestId;
export const selectRequestById = createSelector(
  selectRequests,
  selectRequestId,
  (requests, id) => {
    return requests.find((request) => request.requestId === id);
  }
);
export const selectRequestByType = createSelector(
  selectRequests,
  selectRequestId,
  (requests, type) => {
    return requests.find((request) => request.request === type);
  }
);
export const selectNotification = (state: RootState) =>
  state.session.notification;
export const selectLocationData = (state: RootState) => {
  return locationData[state.session.location];
};
export const selectRoles = (state: RootState) => {
  return state.session.roles;
};

export const selectCanConnect = createSelector(
  selectCell,
  selectRoles,
  (cell, roles) => {
    if (
      cell?.cellName === "BALBRIGGAN" &&
      roles.includes("Pilot-Dashboard-Balbriggan")
    )
      return true;
    if (cell?.cellName === "KERRY" && roles.includes("Pilot-Dashboard-Kerry"))
      return true;
    if (cell?.cellName === "BELAIR" && roles.includes("Pilot-Dashboard-Kerry"))
      return true;
    if (cell?.cellName && roles.includes("Pilot-Dashboard-" + cell?.cellName))
      return true;
    return false;
  }
);

export default persistReducer(
  {
    key: "session",
    storage,
    whitelist: ["location", "region", "cell", "regions", "cells"],
  },
  sessionSlice.reducer
);
